import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Box,
  Modal,
  FormHelperText,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import closeIcon from "../../assets/images/closeicon.svg";
import { fieldUser } from "./FieldJson";
import AccessibleTooltip from "../Tooltip/Tooltip.js";
import checkImg from "../../assets/images/check_circle.svg";
import PropTypes from "prop-types";
import ComboBox from "../ComboBox";
import "../../assets/styles/form.scss";

import {
  validateCustomerForm,
  handleAddCustomer,
  handleEditCustomer,
  populateEditData,
  getEmptyCustomerForm,
} from "../../utils/AddEditCustomer.js";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  paddingLeft: 4,
  paddingRight: 4,
};

const AddEditCustomer = (props) => {
  const { customerAddedSucess, customerEditedSuccess, editError, error } =
    useSelector((state) => state.customerData);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(getEmptyCustomerForm());
  const [formError, setFormError] = useState({});
  const [successMessage, setSuccessMessage] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const firstInputRef = useRef(null);

  const {
    setOpen,
    open,
    actionButton,
    setActionButton,
    editData,
    handleClose: handleCloseParentMethod,
  } = props;

  //  Focus handling
  useEffect(() => {
      const timeoutId = setTimeout(() => {
      if (open && firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 200);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [open]);
 useEffect(() => {
    if (successMessage) {
      if (open && firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }
  }, [successMessage]);
  // Escape handling for tooltip
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.key === "Escape" || e.key === "Esc") && tooltipOpen) {
        setTooltipOpen(false);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, tooltipOpen]);

  // 🧩 Input change
  const handleInputChange = (e) => {
    let name, value;
    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 🧩 Validation
  const handleValidation = () => {
    const { isValid, errors } = validateCustomerForm(formData);
    setFormError(errors);
    return isValid;
  };

  // 🧩 Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!handleValidation()) return;

    if (actionButton === "add") {
      await handleAddCustomer(e, formData, dispatch, setFormError);
    } else if (actionButton === "edit") {
      await handleEditCustomer(e, formData, editData, dispatch, setFormError);
    }
  };

  // 🧩 Reset and close modal
  const handleClose = () => {
    setOpen(false);
    handleCloseParentMethod?.();
    setActionButton("");
    setFormError({});
    setSuccessMessage(false);
    setTooltipOpen(false);
    setFormData(getEmptyCustomerForm());
  };

  // 🧩 Handle success
  useEffect(() => {
    if (customerAddedSucess || customerEditedSuccess) {
      setSuccessMessage(true);
    }
  }, [customerAddedSucess, customerEditedSuccess]);

  // 🧩 Handle edit error
  useEffect(() => {
    if (editError && Object.keys(editError).length > 0) {
      setFormError((prev) => ({
        ...prev,
        ...editError,
      }));
    }
  }, [editError]);

  useEffect(() => {
    if (actionButton === "edit" && editData) {
      setFormData(populateEditData(editData));
    } else {
      setFormData(getEmptyCustomerForm());
    }
  }, [actionButton, editData]);

  useEffect(() => {
    if(error === "Email Already In Use") { 
      setFormError((prev) => ({
        ...prev,
        emailAddress: "Email already In use"
      }));
    } else if (error && typeof error === "string") {
      setFormError({ formLevel: error });
    }
  }, [error]);

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      disableEscapeKeyDown={tooltipOpen}
      className={`globalForm ${successMessage ? "userSuccessAdded" : ""}`}
    >
      <Box sx={style} className="mainModalBox" role="dialog" aria-modal="true">
        {/* Accessibility screen reader text */}
        <span aria-live="polite" className="sr-only">
          {Object.values(formError).filter(Boolean).join(", ")}
        </span>

        {/* Header */}
        <div
          className="Modal_header"
          style={
            successMessage
              ? { display: "flex", justifyContent: "flex-end", marginTop: 15 }
              : {}
          }
        >
          {!successMessage && (
            <h1 id="add-edit-customer-title">
              {actionButton === "edit" ? "Edit Customer" : "Create Customer"}
            </h1>
          )}
          <AccessibleTooltip title="Close" open={tooltipOpen}>
            <button
              ref={firstInputRef}
              onClick={() => handleClose()}
              data-testid="close-button"
              className="close_button_modal"
              aria-label="Close"
              onFocus={() => setTooltipOpen(true)}
              onBlur={() => setTooltipOpen(false)}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
            >
              <img src={closeIcon} alt="" aria-hidden="true" />
            </button>
          </AccessibleTooltip>
        </div>

        {/* Form Section */}
        <div className="Modal_middle" id="add-edit-customer-description">
          {!successMessage ? (
            <Box component="form" noValidate autoComplete="off">
              {fieldUser.map((data) => {
                if (data.type === "input") {
                  return (
                    <TextField
                      key={data.id || data.name}
                      id={data.id}
                      data-testid={data.id}
                      name={data.name}
                      label={data.label}
                      variant="outlined"
                      onChange={handleInputChange}
                      value={formData[data.name] || ""}
                      error={!!formError[data.name]}
                      helperText={formError[data.name] || data.helperText}
                      fullWidth
                      className="innerFields"
                    />
                  );
                }

                if (data.type === "select") {
                  return (
                    <FormControl
                      key={data.id || data.name}
                      fullWidth
                      className="innerFields"
                    >
                      <InputLabel
                        htmlFor={data.id}
                        shrink={false}
                        className="dropDownLabel"
                      >
                        {data.label}
                      </InputLabel>
                      <ComboBox
                        label={
                          data.name === "roleAssignment"
                            ? "Select Role"
                            : "Select Status"
                        }
                        options={
                          data.name === "roleAssignment"
                            ? ["SUPER_ADMIN", "MODERATOR"]
                            : ["ACTIVE", "INACTIVE"]
                        }
                        value={formData[data.name] || ""}
                        onChange={(value) =>
                          handleInputChange({ name: data.name, value })
                        }
                        classes="customCombo"
                      />
                      <FormHelperText id={`${data.id}-helper-text`}>
                        {formError[data.name]}
                      </FormHelperText>
                    </FormControl>
                  );
                }

                return null;
              })}
            </Box>
          ) : (
            <div
              className="emailsent-wrapper manageUser_details"
              role="alert"
              aria-live="polite"
            >
              <img src={checkImg} alt="" aria-hidden="true" />
              <h2>Success</h2>
              <p>
                Customer has been{" "}
                {actionButton === "add" ? "created" : "updated"} successfully.
              </p>
              <Button
                className="btnSubmit mu_back"
                onClick={() => handleClose()}
              >
                Return to Customers
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        {!successMessage && (
          <div className="Modal_footer">
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                className="cancelBtn"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="saveBtn"
                onClick={(e) => handleSubmit(e)}
              >
                Save
              </Button>
            </Box>
          </div>
        )}
      </Box>
    </Modal>
  );
};

AddEditCustomer.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  actionButton: PropTypes.oneOf(["add", "edit"]).isRequired,
  setActionButton: PropTypes.func.isRequired,
  editData: PropTypes.shape({
    customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customerName: PropTypes.string,
    emailaddress: PropTypes.string,
    phoneNumber: PropTypes.string,
    alternateNumber: PropTypes.string,
    billingAddress: PropTypes.string,
  }),
  handleClose: PropTypes.func.isRequired,
};

export default AddEditCustomer;
